import React from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';
import {getSizedImageUrl} from '@shopify/theme-images';

class CartItem extends React.Component {
  /* eslint-disable @shopify/react-prefer-private-members */
  handleChange = async (evt, key, qty) => {
    const updateCart = this.props.updateCart;
    const data = {};
    data[key] = qty;
    const url = `${this.state.rootUrl}/cart/update.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({updates: data}),
    };
    try {
      const response = await fetch(url, options);
      const jsonData = await response.json();
      updateCart(jsonData);
    } catch (err) {
      window.console.error(err);
    }
  };

  /* eslint-disable react/state-in-constructor */
  constructor(props) {
    super(props);
    this.decrementQty = this.decrementQty.bind(this);
    this.incrementQty = this.incrementQty.bind(this);
    this.handleQuantityInputBlur = this.handleQuantityInputBlur.bind(this);
    this.addWishlistItem = this.addWishlistItem.bind(this);
    this.removeWishlistItem = this.removeWishlistItem.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.state = {
      rootUrl: window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '',
      qty: props.item.quantity,
      qtyError: '',
      productWishlisted: false,
      pricingPanelExpanded: false,
    };
    this.isCartPage = props.isCartPage;
    this.item = props.item;
    this.imageSize = this.isCartPage ? '288x360' : '192x240';

    this.pricingContainerRef = React.createRef();
    this.pricingPanelRef = React.createRef();
  }
  /* eslint-enable react/state-in-constructor */

  decrementQty(qty, item, min) {
    const newQty = qty <= 0 ? 0 : Math.max(qty, min);
    this.setState({qty: newQty});
    this.handleChange(null, item.variant_id, newQty);
  }

  incrementQty(qty, item, max) {
    const newQty = max ? Math.min(qty, max) : qty;
    this.setState({qty: newQty});
    this.handleChange(null, item.variant_id, newQty);
  }

  handleQuantityInputBlur(event, step, min, max) {
    const inputValue = Number(event.target.value);

    if (this.item.quantity === Number(inputValue)) {
      return;
    }

    const isValidQuantity = inputValue % step === 0;

    let newQty = inputValue;

    let errorMessage = '';

    if (inputValue < min) {
      newQty = min;
      errorMessage = window.theme?.strings?.qtyMinError?.replace('{{ quantity }}', min);
    } else if (max && inputValue > max) {
      newQty = max;
      errorMessage = window.theme?.strings?.qtyMaxError?.replace('{{ quantity }}', max);
    } else if (!isValidQuantity) {
      // Get the nearest valid quantity, and check against min and max values
      let validQty = Math.round(inputValue / step) * step;
      if (max) {
        validQty = Math.min(validQty, max);
      }
      newQty = Math.max(min, validQty);
      errorMessage = window.theme?.strings?.qtyIncrementError?.replace('{{ quantity }}', step);
    }

    if (!newQty) return;

    this.setState({
      qty: newQty,
      qtyError: errorMessage,
    });

    this.handleChange(null, this.item.variant_id, newQty);
  }

  handleClickOutside(event) {
    if (
      !this.state.pricingPanelExpanded ||
      !this.pricingContainerRef.current ||
      this.pricingContainerRef.current.contains(event.target)
    )
      return;

    this.setState({
      pricingPanelExpanded: false,
    });
  }

  componentDidMount() {
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    // Handle volume pricing collapse on click outside
    document.addEventListener('side-cart:open', () => {
      document.addEventListener('click', this.handleClickOutside);
    });
    document.addEventListener('side-cart:closed', () => {
      document.removeEventListener('click', this.handleClickOutside);
    });
  }

  addWishlistItem(item) {
    window._swat.addToWishList(
      {
        epi: item.variant_id,
        du: `${window.location.origin}${item.url}`,
        empi: item.product_id,
        iu: item.featured_image.url,
        pr: item.price,
        stk: 100,
      },
      () => {
        this.setState({
          productWishlisted: true,
        });
      },
      setTimeout(() => {
        this.handleChange(null, item.variant_id, 0);
      }, 1200)
    );
  }

  removeWishlistItem(item) {
    window._swat.removeFromWishList(
      {
        epi: item.variant_id,
        du: `${window.location.origin}${item.url}`,
        empi: item.product_id,
        iu: item.featured_image.url,
        pr: item.price,
        stk: 100,
      },
      () => {
        this.setState({
          productWishlisted: false,
        });
      }
    );
  }

  /* eslint-enable @shopify/react-prefer-private-members */
  render() {
    const {item, customData} = this.props;
    const wishlistTextDesktop = this.state.productWishlisted
      ? window.theme.strings.savedForLater
      : window.theme.strings.saveForLater;
    const wishlistTextMobile = this.state.productWishlisted ? window.theme.strings.saved : window.theme.strings.save;

    const isSubscriptionProduct = item.selling_plan_allocation || item.properties?.shipping_interval_frequency;
    const shippingIntervalFrequency =
      item.properties?._shipping_interval_frequency || item.properties?.shipping_interval_frequency;
    const shippingIntervalUnitType =
      item.properties?._shipping_interval_unit_type || item.properties?.shipping_interval_unit_type;

    const quantityIncrement = customData?.quantityRule?.increment ?? 1;
    const quantityMin = customData?.quantityRule?.min ?? 1;
    const quantityMax = customData?.quantityRule?.max ?? null;

    return (
      <li className="cart-item">
        <div className="cart-item__content-wrapper">
          <div className="cart-item__image">
            <a href={item.url}>
              <img
                loading="lazy"
                width="192"
                height="240"
                src={getSizedImageUrl(item.image, this.imageSize)}
                alt={item.title}
              />
            </a>
          </div>
          <div className="cart-item__content">
            <div className="cart-item__content-group">
              <div className="cart-item__head">
                <p className="cart-item__title">
                  {isSubscriptionProduct &&
                    item.properties._productTitle &&
                    item.properties._productTitle !== null &&
                    item.properties._productTitle.replace(' - ', ' ')}
                  {!isSubscriptionProduct && item.product_title.replace(' - ', ' ')}
                </p>
                <p className="cart-item__description">{item.variant_title}</p>
                {!this.isCartPage && isSubscriptionProduct && (
                  <>
                    <p className="cart-item__subscription p-t-sm">{`${window.theme.strings.deliverEvery} ${shippingIntervalFrequency} ${shippingIntervalUnitType}`}</p>
                  </>
                )}
                {item.discounts.length > 0 &&
                  item.discounts.map((discount) => (
                    <p className="cart-item__description muted" key={discount.title}>
                      {discount.title} (-{formatMoney(discount.amount, window.theme.moneyFormat)})
                    </p>
                  ))}
              </div>
              <div className="cart-item__content-group-row">
                {item.line_price > 0 && (
                  <div className="quantity-selector">
                    <label htmlFor={item.product_title}>QTY.</label>
                    <button
                      onClick={() => this.decrementQty(this.state.qty - quantityIncrement, item, quantityMin)}
                      className="btn--icon"
                      type="button"
                    >
                      <svg
                        class="icon icon-minus"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 12H18"
                          stroke="currentColor"
                          strokeLinecap="square"
                          strokeWidth="1px"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                    </button>
                    <input
                      id={item.product_title}
                      type="number"
                      name="quantity"
                      placeholder={this.state.qty}
                      min={quantityMin}
                      step={quantityIncrement}
                      max={quantityMax}
                      aria-label={item.product_title}
                      value={this.state.qty}
                      onBlur={(event) =>
                        this.handleQuantityInputBlur(event, quantityIncrement, quantityMin, quantityMax)
                      }
                    />
                    <button
                      onClick={() => this.incrementQty(this.state.qty + quantityIncrement, item, quantityMax)}
                      className="btn--icon"
                      type="button"
                      disabled={
                        quantityMax &&
                        (this.state.qty >= quantityMax || this.state.qty + quantityIncrement > quantityMax)
                      }
                    >
                      <svg
                        class="icon icon-plus"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M12,6V18"
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="square"
                          strokeWidth="1px"
                          vectorEffect="non-scaling-stroke"
                        />
                        <path
                          d="M6,12H18"
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="square"
                          strokeWidth="1px"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                    </button>
                  </div>
                )}
                {item.total_discount === 0 ? (
                  <span className="cart-item__price">
                    {formatMoney(item.line_price, window.theme.moneyFormat)}
                    {window.Shopify.currency.active}
                  </span>
                ) : (
                  <div className="cart-item__price-discounted">
                    <span className="cart-item__price cart-item__price-original">
                      {formatMoney(item.original_line_price, window.theme.moneyFormat)}
                      {window.Shopify.currency.active}
                    </span>
                    <span className="cart-item__price">
                      {formatMoney(item.line_price, window.theme.moneyFormat)}
                      {window.Shopify.currency.active}
                    </span>
                  </div>
                )}
              </div>

              {this.state.qtyError && (
                <p class="cart-item__quantity-error navigation-text" data-qty-error>
                  {this.state.qtyError}
                </p>
              )}

              {customData?.quantityPriceBreaks?.length > 0 && (
                <div className="cart-item__content-group-row cart-item__content-group-row--full">
                  <div className="cart-item__pricing" ref={this.pricingContainerRef}>
                    <button
                      type="button"
                      className="cart-item__pricing-toggle navigation-text btn-none"
                      aria-expanded={this.state.pricingPanelExpanded}
                      onClick={() => {
                        this.setState((state) => ({
                          pricingPanelExpanded: !state.pricingPanelExpanded,
                        }));
                      }}
                    >
                      <svg
                        class="icon icon-information"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#8348C813)">
                          <path
                            d="M8.53044 13.8348C8.39131 13.8348 8.25217 13.7826 8.14782 13.6957C7.97391 13.5565 7.88696 13.3304 7.93913 13.1043L8.79131 8.75652L8.32174 9.01739C8.02608 9.17391 7.66088 9.08697 7.48697 8.79131C7.33044 8.49566 7.41739 8.13044 7.71304 7.95652L9.33044 7.03477C9.53913 6.91304 9.79999 6.93044 9.9913 7.06957C10.1826 7.2087 10.2696 7.45218 10.2348 7.67827L9.34783 12.1826L10.0783 11.8696C10.3913 11.7304 10.7391 11.8696 10.8783 12.1826C11.0174 12.4957 10.8783 12.8435 10.5652 12.9826L8.7739 13.7652C8.70434 13.8174 8.6174 13.8348 8.53044 13.8348ZM10.2522 5.52174C10.4087 5.36522 10.513 5.13913 10.513 4.91304C10.513 4.68696 10.4261 4.46087 10.2522 4.30435C10.0956 4.14783 9.86956 4.04348 9.64347 4.04348C9.41738 4.04348 9.1913 4.13044 9.03477 4.30435C8.87825 4.46087 8.7739 4.68696 8.7739 4.91304C8.7739 5.13913 8.86086 5.36522 9.03477 5.52174C9.1913 5.67826 9.41738 5.78261 9.64347 5.78261C9.86956 5.78261 10.0956 5.69565 10.2522 5.52174ZM17 9C17 4.58261 13.4174 1 9 1C4.58261 1 1 4.58261 1 9C1 13.4174 4.58261 17 9 17C13.4174 17 17 13.4174 17 9ZM15.6087 9C15.6087 12.6522 12.6522 15.6087 9 15.6087C5.34783 15.6087 2.3913 12.6522 2.3913 9C2.3913 5.34783 5.34783 2.3913 9 2.3913C12.6522 2.3913 15.6087 5.34783 15.6087 9Z"
                            fill="black"
                          />
                        </g>
                        <defs>
                          <clipPath id="8348C813">
                            <rect width="16" height="16" fill="white" transform="translate(1 1)" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span>{window.theme?.strings?.cartVolumePricingTitle}</span>
                      <span className="cart-item__pricing-indicator" />
                    </button>
                    <div
                      className="cart-item__pricing-panel"
                      ref={this.pricingPanelRef}
                      style={
                        this.state.pricingPanelExpanded
                          ? {height: `${this.pricingPanelRef.current?.scrollHeight}px`}
                          : {height: '0'}
                      }
                    >
                      <div className="cart-item__pricing-panel-inner">
                        <p className="cart-item__pricing-title navigation-text">
                          <span>
                            {window.theme?.strings?.qtyIncrement?.replace('{{ quantity }}', quantityIncrement)}
                          </span>
                          <span>{window.theme?.strings?.qtyMin?.replace('{{ quantity }}', quantityMin)}</span>
                        </p>
                        <ul className="volume-pricing__list navigation-text list-none">
                          {customData.quantityPriceBreaks.map((priceBreak) => (
                            <li className="volume-pricing__item" key={priceBreak.price}>
                              <span className="volume-pricing__qty">
                                {priceBreak.minQuantity}
                                <small>+</small>
                              </span>
                              <span>{formatMoney(priceBreak.price, theme.moneyFormat)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="cart-item__content cart-item__content--end">
              <div className="cart-item__subscription--wrapper">
                {this.isCartPage && isSubscriptionProduct && (
                  <>
                    <p className="cart-item__subscription">{`${window.theme.strings.deliverEvery} ${shippingIntervalFrequency} ${shippingIntervalUnitType}`}</p>
                  </>
                )}
                {this.isCartPage && window.theme.wishlistEnabled && (
                  <button
                    type="button"
                    className={`cart-item__save btn btn--text m-t-md${
                      this.state.productWishlisted ? ' wishlisted' : ''
                    }`}
                    onClick={() =>
                      this.state.productWishlisted ? this.removeWishlistItem(item) : this.addWishlistItem(item)
                    }
                  >
                    <svg
                      className="icon icon-heart"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20.4578 4.59133C19.9691 4.08683 19.3889 3.68663 18.7503 3.41358C18.1117 3.14054 17.4272 3 16.7359 3C16.0446 3 15.3601 3.14054 14.7215 3.41358C14.0829 3.68663 13.5026 4.08683 13.0139 4.59133L11.9997 5.63785L10.9855 4.59133C9.99842 3.57276 8.6596 3.00053 7.26361 3.00053C5.86761 3.00053 4.52879 3.57276 3.54168 4.59133C2.55456 5.6099 2 6.99139 2 8.43187C2 9.87235 2.55456 11.2538 3.54168 12.2724L4.55588 13.3189L11.9997 21L19.4436 13.3189L20.4578 12.2724C20.9467 11.7681 21.3346 11.1694 21.5992 10.5105C21.8638 9.85148 22 9.14517 22 8.43187C22 7.71857 21.8638 7.01225 21.5992 6.35328C21.3346 5.69431 20.9467 5.09559 20.4578 4.59133V4.59133Z"
                        stroke="currentColor"
                        strokeWidth="1px"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                    <span className="medium-down--hide">{wishlistTextDesktop}</span>
                    <span className="large-up--hide">{wishlistTextMobile}</span>
                  </button>
                )}
              </div>
              <button
                className="btn btn--text cart-item__remove"
                onClick={(evt) => {
                  if (item.properties?._gwp) {
                    const manuallyRemovedGifts = JSON.parse(sessionStorage.getItem('manuallyRemovedGifts')) || [];
                    manuallyRemovedGifts.push(item.product_id);
                    sessionStorage.setItem(
                      'manuallyRemovedGifts',
                      JSON.stringify(Array.from(new Set(manuallyRemovedGifts)))
                    );
                  }
                  this.handleChange(evt, item.key, 0);
                }}
                type="button"
              >
                <svg
                  class="icon icon-close"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M17,7,7,17"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeWidth="1px"
                    vectorEffect="non-scaling-stroke"
                  />
                  <path
                    d="M7,7,17,17"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeWidth="1px"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                Remove
              </button>
            </div>
          </div>
        </div>
      </li>
    );
  }
}

CartItem.propTypes = {
  item: PropTypes.object,
  customData: PropTypes.object,
  updateCart: PropTypes.func,
  isCartPage: PropTypes.bool,
};

export default CartItem;
